/**
 * Optimized Context Extractor
 * Implements Hybrid Approach for Redux state optimization:
 * 1. ALWAYS send: Workflow state (small, ~5-10KB)
 * 2. CONDITIONALLY send: Page-specific data (only when relevant)
 * 3. NEVER send: Content/labeling fields (saves ~400KB-1.5MB)
 * 
 * This replaces the old contextExtractor.ts to avoid duplication
 */

import { RootState } from '../../../reducers';

/**
 * Context interface for Flexit Application
 * This provides the LLM with information about where the user is and what they're doing
 */
export interface FlexitApplicationContext {
  // Optimized Redux context (Hybrid Approach)
  // Only includes essential workflow + page-specific data
  // Excludes all content/labeling fields (saves 85-95% payload)
  optimizedRedux: OptimizedReduxContext;
}

/**
 * Minimal workflow state - ALWAYS sent with every message
 */
interface WorkflowState {
  currentStep: string | null;
  stepDescription: string | null;
  isLifeEvent: boolean;
  canProceedNext: boolean;
  hasErrors: boolean;
  showWarning: boolean;
  basePageTitle: string | null;
}

/**
 * Benefit state - CONDITIONALLY sent (only on benefit pages)
 * Sends only flags/indicators, not complete data
 */
interface BenefitState {
  hasBenefits: boolean;
  hasFlexDollars: boolean;
  benefitCount: number;
}

/**
 * Profile state - CONDITIONALLY sent (only on profile pages)
 * Sends only flags/indicators, not complete data
 */
interface ProfileState {
  hasPersonalInfo: boolean;
  hasDependents: boolean;
  dependentCount: number;
}

/**
 * Coverage state - CONDITIONALLY sent (only on coverage pages)
 * Sends only flags/indicators, not complete data
 */
interface CoverageState {
  hasCurrent: boolean;
  hasPast: boolean;
}

/**
 * Beneficiary state - CONDITIONALLY sent (only on beneficiary pages)
 * Sends only flags/indicators, not complete data
 */
interface BeneficiaryState {
  hasBeneficiaries: boolean;
  beneficiaryCount: number;
}

/**
 * Main optimized Redux context
 */
export interface OptimizedReduxContext {
  workflow: WorkflowState;
  pageSpecific?: {
    benefit?: BenefitState;
    profile?: ProfileState;
    coverage?: CoverageState;
    beneficiary?: BeneficiaryState;
  };
}

/**
 * Extract optimized context from Redux state
 * Uses hybrid approach: always send workflow, conditionally send page-specific data
 */
export function extractOptimizedContext(
  state: RootState,
  currentPath: string
): OptimizedReduxContext {
  // 1. ALWAYS extract workflow state (small, essential)
  const workflow = extractWorkflowState(state);
  
  // 2. CONDITIONALLY extract page-specific data based on current route
  const pageSpecific = extractPageSpecificData(state, currentPath);
  
  return {
    workflow,
    ...(pageSpecific && { pageSpecific })
  };
}

/**
 * Extract minimal workflow state (always sent)
 * This provides essential context about where user is and what they're doing
 */
function extractWorkflowState(state: RootState): WorkflowState {
  return {
    currentStep: state.event?.progressBar?.labelText || null,
    stepDescription: state.event?.stepDescription || null,
    isLifeEvent: state.event?.nextEnrolStep?.isLifeEvent || false,
    canProceedNext: !state.event?.disableNext,
    hasErrors: detectValidationErrors(),
    showWarning:  false,
    basePageTitle: state.event?.basePageTitle || null
  };
}

/**
 * Extract page-specific data based on current route
 * Only includes data relevant to the current page
 */
function extractPageSpecificData(
  state: RootState,
  currentPath: string
): OptimizedReduxContext['pageSpecific'] | undefined {
  const path = currentPath.toLowerCase();
  
  // Benefit pages - include benefit flags only
  if (path.includes('/benefit') || path.includes('/health') || path.includes('/life')) {
    return {
      benefit: {
        hasBenefits: !!state.benefit?.benefitsLists && state.benefit.benefitsLists.length > 0,
        hasFlexDollars: !!state.benefit?.flexDollars,
        benefitCount: state.benefit?.benefitsLists?.length || 0
      }
    };
  }
  
  // Profile pages - include profile flags only
  if (path.includes('/profile') || path.includes('/dependent')) {
    return {
      profile: {
        hasPersonalInfo: !!state.profile?.personalInfo,
        hasDependents: !!state.profile?.dependents && state.profile.dependents.length > 0,
        dependentCount: state.profile?.dependents?.length || 0
      }
    };
  }
  
  // Coverage pages - include coverage flags only
  if (path.includes('/coverage')) {
    return {
      coverage: {
        hasCurrent: !!state.coverage?.currentCoverage,
        hasPast: !!state.coverage?.pastCoverage
      }
    };
  }
  
  // Beneficiary pages - include beneficiary flags only
  if (path.includes('/beneficiar')) {
    return {
      beneficiary: {
        hasBeneficiaries: !!state.beneficiary?.beneficiariesInfo,
        beneficiaryCount: Array.isArray(state.beneficiary?.beneficiariesInfo) 
          ? state.beneficiary.beneficiariesInfo.length 
          : (state.beneficiary?.beneficiariesInfo ? 1 : 0)
      }
    };
  }
  
  // No page-specific data needed for other pages
  return undefined;
}

/**
 * Helper to detect validation errors from DOM
 */
function detectValidationErrors(): boolean {
  const errorSelectors = [
    '.invalid-feedback',
    '.error-message',
    '.text-danger',
    '.alert-danger'
  ];
  
  return errorSelectors.some(selector => {
    const elements = document.querySelectorAll(selector);
    // Check if any error elements are visible
    return Array.from(elements).some(el => {
      const htmlEl = el as HTMLElement;
      return htmlEl.offsetParent !== null;
    });
  });
}

/**
 * Extract application context from Redux state
 * This is called when the user sends a chat message
 * 
 * Returns only optimizedRedux to avoid duplication with page context
 */
export const extractApplicationContext = (state: RootState): FlexitApplicationContext => {
  // Extract optimized Redux context (Hybrid Approach)
  // This contains all necessary workflow and page-specific data
  const optimizedRedux = extractOptimizedContext(state, window.location.pathname);
  
  return {
    optimizedRedux
  };
};

/**
 * Format context for display (optional - for debugging)
 */
export const formatContextForDisplay = (context: FlexitApplicationContext): string => {
  const parts: string[] = [];
  const redux = context.optimizedRedux;
  
  if (redux.workflow.currentStep) {
    parts.push(`📊 ${redux.workflow.currentStep}`);
  }
  
  if (redux.workflow.stepDescription) {
    parts.push(`📝 ${redux.workflow.stepDescription}`);
  }
  
  if (redux.workflow.isLifeEvent) {
    parts.push(`🔄 Life Event`);
  }
  
  if (redux.pageSpecific?.benefit?.hasBenefits) {
    parts.push(`💼 ${redux.pageSpecific.benefit.benefitCount} Benefits`);
  }
  
  if (redux.pageSpecific?.profile?.hasPersonalInfo) {
    parts.push(`👤 Profile`);
  }
  
  if (redux.workflow.hasErrors) {
    parts.push(`⚠️ Has Errors`);
  }
  
  if (redux.workflow.showWarning) {
    parts.push(`⚠️ Warning`);
  }
  
  return parts.join(' • ');
};

/**
 * Calculate payload size for monitoring
 */
export function calculatePayloadSize(context: any): {
  totalKB: number;
  optimizedKB: number;
  savingsPercent: number;
} {
  const totalSize = JSON.stringify(context).length;
  const optimizedSize = context.additional_data?.optimizedRedux 
    ? JSON.stringify(context.additional_data.optimizedRedux).length 
    : 0;
  
  return {
    totalKB: totalSize / 1024,
    optimizedKB: optimizedSize / 1024,
    savingsPercent: optimizedSize > 0 
      ? ((totalSize - optimizedSize) / totalSize) * 100 
      : 0
  };
}
